import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useCategories, useSEO } from "../../hooks";
import { serviceAPI } from "../../services/api";
import ServiceCard from "./ServiceCard";
import { FiSearch, FiFilter, FiX } from "react-icons/fi";
import "./ServicesPage.css";

const SORT_OPTIONS = [
  { value: "", label: "Most Popular" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "rating", label: "Highest Rated" },
];

const ServicesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { categories } = useCategories();

  // Always derive state from URL — this makes navbar links work correctly
  const urlQ = searchParams.get("q") || "";
  const urlCategory = searchParams.get("category") || "";

  const [searchInput, setSearchInput] = useState(urlQ);
  const [sort, setSort] = useState("");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  // Sync input box when URL changes (e.g. clicking a navbar link)
  useEffect(() => {
    setSearchInput(urlQ);
    setPage(1);
  }, [urlQ, urlCategory]);

  // Fetch whenever URL params, sort, or page change
  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (urlQ && urlQ.trim().length >= 2) {
        res = await serviceAPI.search({
          q: urlQ.trim(),
          city: "kolkata",
          page,
          limit: 12,
        });
      } else {
        const params = { city: "kolkata", page, limit: 12 };
        if (urlCategory) params.category = urlCategory;
        res = await serviceAPI.getAll(params);
      }

      let list = res.data.data || [];
      if (sort === "price_asc")
        list = [...list].sort((a, b) => a.basePrice - b.basePrice);
      if (sort === "price_desc")
        list = [...list].sort((a, b) => b.basePrice - a.basePrice);
      if (sort === "rating")
        list = [...list].sort((a, b) => b.averageRating - a.averageRating);

      setServices(list);
      setMeta(res.data.meta || null);
    } catch (err) {
      console.error("Services fetch error:", err);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [urlQ, urlCategory, sort, page]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // ── Handlers ────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    const params = {};
    if (searchInput.trim()) params.q = searchInput.trim();
    if (urlCategory) params.category = urlCategory;
    setSearchParams(params);
    setPage(1);
  };

  const handleCategoryChange = (catSlug) => {
    const next = catSlug === urlCategory ? "" : catSlug;
    const params = {};
    if (next) params.category = next;
    setSearchParams(params); // clears q too — fresh browse by category
    setPage(1);
    setMobileFilterOpen(false);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchParams({});
    setPage(1);
  };

  const hasFilters = urlQ || urlCategory;

  // ── Dynamic SEO ────────────────────────────────────────
  const activeCategoryObj = categories.find((c) => c.slug === urlCategory);
  const seoTitle = urlQ
    ? `"${urlQ}" Services in Kolkata`
    : urlCategory && activeCategoryObj
      ? `${activeCategoryObj.name} Services in Kolkata`
      : "All Home Services in Kolkata";

  const seoDescription = urlQ
    ? `Find ${urlQ} services in Kolkata by verified experts. Affordable pricing, pay after service. Book now on Expert At Door.`
    : urlCategory && activeCategoryObj
      ? `Book professional ${activeCategoryObj.name} services in Kolkata. Verified experts, transparent pricing, zero upfront payment. Expert At Door.`
      : "Browse 20+ professional home services in Kolkata — cleaning, laptop repair, printer repair, electrician, car wash & more. Pay only after service. 4.7★ rated.";

  const seoCanonical = urlCategory
    ? `/services?category=${urlCategory}`
    : "/services";

  useSEO({
    title: seoTitle,
    description: seoDescription,
    canonical: seoCanonical,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.Expert At Door.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://www.Expert At Door.com/services",
        },
        ...(activeCategoryObj
          ? [
              {
                "@type": "ListItem",
                position: 3,
                name: activeCategoryObj.name,
                item: `https://www.Expert At Door.com/services?category=${urlCategory}`,
              },
            ]
          : []),
      ],
    },
  });

  return (
    <div className="services-page">
      {/* Header */}
      <header className="services-header">
        <div className="container">
          <h1>Services in Kolkata</h1>
          <p>
            Professional experts for cleaning, laptop repair, printer repair,
            electrician, dress ironing, hair cutting services, and more
          </p>

          <form
            className="services-search"
            onSubmit={handleSearch}
            role="search"
            aria-label="Search services in Kolkata"
          >
            <div className="services-search-inner">
              <FiSearch size={16} className="search-icon" aria-hidden="true" />
              <input
                type="search"
                name="q"
                placeholder="Search — car wash, laptop repair, printer service, chimney cleaning..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search home services"
              />
              {searchInput && (
                <button
                  type="button"
                  className="clear-search"
                  onClick={() => {
                    setSearchInput("");
                    setSearchParams(
                      urlCategory ? { category: urlCategory } : {},
                    );
                  }}
                  aria-label="Clear search"
                >
                  <FiX size={13} aria-hidden="true" />
                </button>
              )}
              <button type="submit" className="btn btn-primary btn-sm">
                Search
              </button>
            </div>
          </form>
        </div>
      </header>

      {/* Body */}
      <div className="container services-layout ">
        {/* Sidebar */}
        <aside
          className={`services-sidebar ${mobileFilterOpen ? "mobile-open" : ""}`}
        >
          <div className="sidebar-header">
            <h3>Filters</h3>
            {hasFilters && (
              <button className="clear-filters-btn" onClick={clearFilters}>
                Clear all
              </button>
            )}
          </div>

          <div className="filter-section">
            <h4 className="filter-title">Category</h4>
            <div className="filter-options">
              <button
                className={`filter-opt ${!urlCategory ? "active" : ""}`}
                onClick={() => handleCategoryChange("")}
              >
                All Services
              </button>
              {categories.map((cat) => (
                <button
                  key={cat._id}
                  className={`filter-opt ${urlCategory === cat.slug ? "active" : ""}`}
                  onClick={() => handleCategoryChange(cat.slug)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-section">
            <h4 className="filter-title">City</h4>
            <div className="filter-options">
              <button className="filter-opt active">Kolkata</button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="services-main">
          <div className="services-toolbar">
            <div className="services-count">
              {loading
                ? "Loading..."
                : `${meta?.total ?? services.length} service${(meta?.total ?? services.length) !== 1 ? "s" : ""} found`}
            </div>
            <div className="toolbar-right">
              <select
                className="sort-select"
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                className="mobile-filter-btn hide-desktop"
                onClick={() => setMobileFilterOpen(true)}
              >
                <FiFilter size={14} /> Filters
              </button>
            </div>
          </div>

          {/* Active chips */}
          {hasFilters && (
            <div className="active-filters">
              {urlQ && (
                <span className="filter-tag">
                  "{urlQ}"
                  <button
                    onClick={() =>
                      setSearchParams(
                        urlCategory ? { category: urlCategory } : {},
                      )
                    }
                  >
                    <FiX size={11} />
                  </button>
                </span>
              )}
              {urlCategory &&
                categories.find((c) => c.slug === urlCategory) && (
                  <span className="filter-tag">
                    {categories.find((c) => c.slug === urlCategory)?.name}
                    <button onClick={() => handleCategoryChange("")}>
                      <FiX size={11} />
                    </button>
                  </span>
                )}
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="services-grid-main">
              {Array(6)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="card skeleton"
                    style={{ height: 290 }}
                  />
                ))}
            </div>
          ) : services.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <h3>No services found</h3>
              <p>
                {urlQ
                  ? `No results for "${urlQ}". Try a different keyword.`
                  : "No services in this category yet."}
              </p>
              <button className="btn btn-outline" onClick={clearFilters}>
                View all services
              </button>
            </div>
          ) : (
            <div className="services-grid-main">
              {services.map((svc) => (
                <ServiceCard key={svc._id} service={svc} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-ghost btn-sm"
                disabled={!meta.hasPrevPage}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="page-info">
                Page {meta.page} of {meta.totalPages}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                disabled={!meta.hasNextPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </main>
      </div>

      {mobileFilterOpen && (
        <div
          className="mobile-filter-overlay"
          onClick={() => setMobileFilterOpen(false)}
        />
      )}
    </div>
  );
};

export default ServicesPage;
